import React, { ReactNode, useEffect, useState } from "react";
import { Resource, Scope } from "../../../../../../types/NendaTypes";
import PermissionsGate from "../../../components/PermissionGate";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { useEffectNotOnMount } from "../../../../../hooks/useEffectNotOnMount";
import { PiTelevision } from "react-icons/pi";
import { Form, Formik } from "formik";
import { FormField } from "../../../../Form/FormField";
import { BasicTable } from "../../../../Table/BasicTable";
import CMSPageHeader from "../../../ui-components/pageLayout/CMSPageHeader";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { store } from "../../../store";
import {
  CreateFeatureSetAction,
  GetFeatureSetsAction,
  getFeatureSetSelector,
  getFeatureSetsSelector,
} from "../../../store/reducers/offeringsReducer";
import { Modal } from "../../../../Modal/Modal";
import { ModalWrapper } from "../../../ui-components/pageLayout/ModalComponents";
import { FeatureSetsPageSettings } from "./FeatureSetsTableSettings";
import { fromJson } from "@client/models/modelHelpers";
import { FeatureSet } from "@client/models/Offerings/featureSet";

interface CreateFeatureSetValues {
  name: string;
}

const CreateFeatureSetSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required*"),
});

const FeatureSets: React.FC = (): ReactNode => {
  const [showForm, setShowForm] = React.useState(false);
  const history = useNavigate();
  const featureSets = useSelector(getFeatureSetsSelector);
  const featureSet = useSelector(getFeatureSetSelector);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const fetchFeatureSets = () => {
    store.dispatch(GetFeatureSetsAction());
  };

  useEffect(() => {
    fetchFeatureSets();
  }, []);

  function submitCreate(values: CreateFeatureSetValues) {
    setIsCreating(true);
    store.dispatch(CreateFeatureSetAction(values.name));
  }

  const handleRowClick = (id: string) => {
    redirect(id);
  };

  useEffectNotOnMount(() => {
    if (featureSet && isCreating) {
      redirect(featureSet._id);
    }
  }, [featureSet]);

  const redirect = (id) => {
    history(`/admin/offerings/feature-sets/${id}`);
  };

  const handleSearch = (searchQuery: string) => {
    store.dispatch(GetFeatureSetsAction(searchQuery));
  };

  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Content,
        scopes: [Scope.CanAdministrate],
      }}
    >
      <>
        <CMSPageHeader
          title={t("customerportal.pages.feature_sets.title")}
          count={featureSets.length}
          button={{
            title: t("customerportal.pages.feature_sets.create"),
            onClick: () => setShowForm(true),
            icon: PiTelevision,
          }}
          onSearch={handleSearch}
        />
        <Formik
          initialValues={{
            name: "",
          }}
          validationSchema={CreateFeatureSetSchema}
          onSubmit={(values: CreateFeatureSetValues) => {
            submitCreate(values);
            setShowForm(false);
          }}
        >
          {({ handleSubmit }) => (
            <Form>
              <ModalWrapper>
                <Modal
                  style={{}}
                  visible={showForm}
                  onClose={() => setShowForm(false)}
                  title={"Create new feature set"}
                  onSubmit={handleSubmit}
                  confirmText="Create"
                  closeOnSubmit={false}
                >
                  <FormField
                    name="name"
                    label="name"
                    placeholder="Feature set name..."
                  />
                </Modal>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
        <BasicTable
          tableSettings={FeatureSetsPageSettings}
          tableData={featureSets.map((featureSet) => {
            return fromJson(FeatureSet, featureSet);
          })}
          onRowClick={handleRowClick}
        />
      </>
    </PermissionsGate>
  );
};

export default FeatureSets;
